import {
  AfterViewChecked,
  Component,
  DestroyRef,
  OnInit,
  ViewEncapsulation,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject, fromEvent } from 'rxjs';
import { throwError as observableThrowError } from 'rxjs/internal/observable/throwError';
import { catchError, takeUntil } from 'rxjs/operators';
import { IamBackendService } from '@core/providers/backend/iam-backend.service';
import { NotificationService } from '@core/providers/notification/notification.service';
import { preferredUiLanguage } from '@core/providers/translator/apply-ui-language';
import { EMAIL_REG_EX } from '@shared/validators/custom-validators';
import { AuthService } from './auth.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class LoginComponent implements AfterViewChecked, OnInit {
  private authService = inject(AuthService);
  private activatedRoute = inject(ActivatedRoute);
  private notificationService = inject(NotificationService);
  private translateService = inject(TranslateService);
  private iamBackendService = inject(IamBackendService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  private isResetPasswordSubscribed = false;
  private onFormDestroy$: Subject<void> = new Subject<void>();

  ngAfterViewChecked(): void {
    this.handleResetPassword();
  }

  ngOnInit() {
    this.translateService
      .use(preferredUiLanguage())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() =>
        this.activatedRoute.queryParams
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(params => {
            if (params) {
              // this.resetQueryParams();
              this.logIn();
            }
          })
      );
  }

  private logIn(): void {
    this.authService
      .logIn$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((authLock: Auth0LockStatic) => {
        authLock.on('authenticated', (result: AuthResult) => {
          this.authService.handleAuthenticated(result);
          this.notificationService.dismissSnackBar();
        });
      });
  }

  private handleResetPassword(): void {
    if (this.isInResetPasswordMode() && !this.isResetPasswordSubscribed) {
      this.setAuthMsg(null);
      this.blockSubmitBtnAndHandleResetPassword();
    }
    if (!this.isInResetPasswordMode()) {
      const btnElement = this.getSubmitBtnElement();

      if (btnElement && btnElement.type !== 'submit') {
        this.setBtnSubmitType(btnElement, 'submit');
      }

      this.removeSuccessMsgIfExists();
    } else {
      this.setAuthMsg(null);
    }
  }

  private removeSuccessMsgIfExists(): void {
    const message = document.getElementsByClassName('auth0-global-message-success').item(0);
    const container = message?.parentElement;

    if (message && container && container.children.length > 1) {
      container.removeChild(message);
    }
  }

  private blockSubmitBtnAndHandleResetPassword(): void {
    const btnElement = this.getSubmitBtnElement();

    if (btnElement) {
      this.setBtnSubmitType(btnElement, 'button');
      this.handleOnEmailInputChange();
      fromEvent(btnElement, 'click')
        .pipe(takeUntil(this.onFormDestroy$))
        .subscribe(() => {
          this.sendResetPasswordEmail();
        });
      this.isResetPasswordSubscribed = true;
    }
  }

  private setBtnSubmitType(btnElement: Element, btnType: 'button' | 'submit'): void {
    btnElement.setAttribute('type', btnType);
  }

  private getSubmitBtnElement(): HTMLButtonElement | null {
    return document.querySelector<HTMLButtonElement>('.auth0-lock-submit');
  }

  private sendResetPasswordEmail(): void {
    if (this.isValidEmail()) {
      this.sendEmail();
    } else {
      this.showErrorMsg();
    }
  }

  private goToLoginSection(): void {
    const backBtn = document.getElementById('undefined-back-button');

    if (backBtn) {
      const clickEvent: MouseEvent = new MouseEvent('click', {
        view: window,
        bubbles: true,
        cancelable: false,
      });

      backBtn.dispatchEvent(clickEvent);
    }
  }

  private showResetPasswordResult(): void {
    const text: string = this.translateService.instant(
      'LOGIN.EMAIL_WITH_RESET_PASSWORD_RESULT_MSG'
    );
    const msg = `<div class="auth0-global-message auth0-global-message-success"><span class="animated fadeInUp"><span>${text}</span></span></div>`;

    this.setAuthMsg(msg);
  }

  private showErrorMsgResult(): void {
    const text: string = this.translateService.instant(
      'SHARED.NOTIFICATIONS.ERROR_PROCESSING.TITLE'
    );
    const msg = `<div class="auth0-global-message auth0-global-message-error"><span class="animated fadeInUp"><span>${text}</span></span></div>`;

    this.setAuthMsg(msg);
  }

  private setAuthMsg(msg: string | null): void {
    const container = document.querySelector(
      '#login-auth-container > div > div > form > div > div > div > div > div.auth0-lock-content-body-wrapper > div:nth-child(1) > div'
    );

    if (container) {
      container.innerHTML = msg ?? '';
    }
  }

  private isInResetPasswordMode(): boolean {
    const formElements: HTMLCollectionOf<Element> =
      document.getElementsByClassName('auth0-lock-input-block');

    if (formElements.length === 1) {
      return true;
    }
    if (formElements.length === 3) {
      return false;
    }

    return false;
  }

  private isValidEmail(): boolean {
    return EMAIL_REG_EX.test(this.getEmailInputValue());
  }

  private getEmailInputValue(): string {
    const inputElement = document.getElementById('1-email');

    return inputElement instanceof HTMLInputElement ? inputElement.value : '';
  }

  private handleOnEmailInputChange(): void {
    const emailInput = document.getElementById('1-email');

    if (!emailInput) {
      return;
    }

    fromEvent(emailInput, 'input')
      .pipe(takeUntil(this.onFormDestroy$))
      .subscribe(() => {
        if (this.isValidEmail()) {
          this.hideErrorMsg();
        }
      });
  }

  private showErrorMsg(): void {
    const elements: HTMLCollectionOf<Element> =
      document.getElementsByClassName('auth0-lock-input-block');

    if (elements && elements.length > 0 && elements[0].children.length === 1) {
      elements[0].classList.add('auth0-lock-error');
      elements[0].appendChild(this.getErrorContainer());
    }
  }

  private hideErrorMsg(): void {
    const inputBlock = document.getElementsByClassName('auth0-lock-input-block').item(0);
    const childrenCount = 2;

    if (inputBlock?.lastChild && inputBlock.children.length === childrenCount) {
      inputBlock.removeChild(inputBlock.lastChild);
      inputBlock.classList.remove('auth0-lock-error');
    }
  }

  private getErrorContainer(): Element {
    const element: Element = document.createElement('div');

    element.innerHTML = `<div role="alert" id="auth0-lock-error-msg-email" class="auth0-lock-error-msg"><div class="auth0-lock-error-invalid-hint">${this.translateService.instant(
      'SHARED.FORM_ERRORS.INVALID_EMAIL'
    )}</div></div>`;

    return element;
  }

  private sendEmail(): void {
    this.iamBackendService
      .sendResetPasswordEmail(this.getEmailInputValue())
      .pipe(
        takeUntil(this.onFormDestroy$),
        catchError(() => {
          this.resetForm();
          this.showErrorMsgResult();

          return observableThrowError(null);
        })
      )
      .subscribe(() => {
        this.resetForm();
        this.showResetPasswordResult();
      });
  }

  private resetForm(): void {
    this.hideErrorMsg();
    this.goToLoginSection();
    this.onFormDestroy$.next();
    this.isResetPasswordSubscribed = false;
  }

  private resetQueryParams() {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {},
      queryParamsHandling: 'merge', // remove to replace all query params by provided
    });
  }
}
